import React, { useMemo, useState, useEffect } from 'react';
import Navbar from '../../../../../components/navbar.jsx';
import Footer from '../../../../../components/footer.jsx';
import Button from '../../../../../components/Button.jsx';
import Pagination from '../../../../../components/Pagination.jsx';
import DatePicker from '../../../../../components/DatePicker.jsx';
import { ReportsSidebar, ReportsTable } from '../../components/index.js';
import { getBranchByCode } from '../../../../../api/branchManagement]/branchApi.js';
import PrintReport from '../../components/PrintReport.jsx';
import styles from '../css/HealthnetStatistics.module.css';

const HealthnetStatistics = () => {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [branchInfo, setBranchInfo] = useState(null);

  // Fetch branch information when component mounts
  useEffect(() => {
    const fetchBranchInfo = async () => {
      try {
        // Get user info from localStorage
        const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
        if (userInfo.branchcode) {
          const branchData = await getBranchByCode(userInfo.branchcode);
          setBranchInfo(branchData);
        }
      } catch (error) {
        console.error('Error fetching branch info:', error);
      }
    };

    fetchBranchInfo();
  }, []);

  const rows = useMemo(
    () => [
      {
        scriptDpn: '011205',
        date: '10/06/2023',
        member: 'Elizabeth Lee',
        reference: '745480546',
        authorisation: '13',
        gross: '5317.00',
        payDue: '941.00',
        levy: '0.00',
        other: '851.00',
        levy2: '2871.00',
        medAid: 'PRGN',
      },
      {
        scriptDpn: '554005',
        date: '06/22/2023',
        member: 'Natalia Lopez',
        reference: '48740545',
        authorisation: 'VVIVCGL161',
        gross: '8681.00',
        payDue: '757.00',
        levy: '0.00',
        other: '695.00',
        levy2: '985.00',
        medAid: 'GEMSP',
      },
      {
        scriptDpn: '0112496',
        date: '06/13/2022',
        member: 'Laura Moreno',
        reference: '7810278612',
        authorisation: 'DIR46n',
        gross: '8223.00',
        payDue: '386.00',
        levy: '0.00',
        other: '461.00',
        levy2: '1450.00',
        medAid: 'BANKP',
      },
      {
        scriptDpn: '205842',
        date: '05/01/2022',
        member: 'Elizabeth Hall',
        reference: '54978105',
        authorisation: '',
        gross: '4674.00',
        payDue: 'Reversal Accepted',
        levy: '',
        other: '',
        levy2: '',
        medAid: '',
      },
    ],
    []
  );

  const rowsPerPage = 10;
  const totalPages = Math.max(1, Math.ceil(rows.length / rowsPerPage));
  const safeCurrentPage = Math.min(Math.max(1, currentPage), totalPages);
  const startIndex = (safeCurrentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const pageRows = rows.slice(startIndex, endIndex);

  const columns = useMemo(
    () => [
      { header: 'Script#DPN', accessor: 'scriptDpn' },
      { header: 'Date', accessor: 'date' },
      { header: 'Member', accessor: 'member' },
      { header: 'Reference', accessor: 'reference' },
      { header: 'Authorisation', accessor: 'authorisation' },
      { header: 'Gross', accessor: 'gross' },
      { header: 'Pay Due', accessor: 'payDue' },
      { header: 'Levy', accessor: 'levy' },
      { header: 'Other', accessor: 'other' },
      { header: 'Levy', accessor: 'levy2' },
      { header: 'Med Aid', accessor: 'medAid' },
    ],
    []
  );

  const handlePrint = () => {
    PrintReport({
      reportType: 'healthnet',
      reportTitle: 'HEALTHNET STATISTICS REPORT',
      branchInfo,
      headers: ['Script#DPN', 'Date', 'Member', 'Reference', 'Authorisation', 'Gross', 'Pay Due', 'Levy', 'Other', 'Levy 2', 'Med Aid'],
      data: rows, // Use all rows for print (not just current page)
      dateRange: { fromDate, toDate },
      isPrint: true
    });
  };

  const handleExport = () => {
    PrintReport({
      reportType: 'healthnet',
      reportTitle: 'HEALTHNET STATISTICS REPORT',
      branchInfo,
      headers: ['Script#DPN', 'Date', 'Member', 'Reference', 'Authorisation', 'Gross', 'Pay Due', 'Levy', 'Other', 'Levy 2', 'Med Aid'],
      data: rows, // Use all rows for export (not just current page)
      dateRange: { fromDate, toDate },
      isPrint: false
    });
  };

  return (
    <div className={styles.container}>
      <Navbar backTo="/customer/dispensary" backReplace />

      <div className={styles.mainContent}>
        <div className={styles.layout}>
          <div className={styles.sidebarContainer}>
            <ReportsSidebar />
          </div>

          <div className={styles.content}>
            <div className={styles.reportContainer}>
              <div className={styles.dateFiltersSection}>
                <div className={styles.dateFilters}>
                  <div className={styles.dateFilter}>
                    <label className={styles.dateLabel}>From</label>
                    <DatePicker
                      selectedDate={fromDate}
                      onDateChange={setFromDate}
                      placeholder=""
                      className={styles.dateInput}
                      disableFutureDates={true}
                    />
                  </div>

                  <div className={styles.dateFilter}>
                    <label className={styles.dateLabel}>To</label>
                    <DatePicker
                      selectedDate={toDate}
                      onDateChange={setToDate}
                      placeholder=""
                      className={styles.dateInput}
                      disableFutureDates={true}
                    />
                  </div>
                </div>
              </div>

              <div className={styles.actionButtonsSection}>
                <div className={styles.actionButtons}>
                  <Button onClick={handlePrint} variant="approve" className={styles.printButton}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 9V2h12v7" />
                      <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" />
                      <rect x="6" y="14" width="12" height="8" />
                    </svg>
                    Print
                  </Button>

                  <Button onClick={handleExport} variant="primary" className={styles.exportButton}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                      <polyline points="7,10 12,15 17,10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    Export
                  </Button>
                </div>
              </div>

              <div className={styles.tableWrapper}>
                <ReportsTable columns={columns} data={pageRows} striped={true} />
              </div>

              <div className={styles.paginationContainer}>
                <Pagination
                  currentPage={safeCurrentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default HealthnetStatistics;
